import React, { useState, useEffect } from 'react';
import './tablecard.scss';
import Dialog from '@mui/material/Dialog';
import QRCode from 'react-qr-code';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { Print } from '@mui/icons-material';

const Tablecard = ({ onSelect }) => {
  const [tableData, setTableData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrPopupOpen, setQrPopupOpen] = useState(false);
  const [qrCodeUrl, setQRCodeUrl] = useState('');
  const [tableNo, setTableNo] = useState('');
  const [selectedTableId, setSelectedTableId] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL;
  const orderAppUrl = process.env.REACT_APP_ORDER_APP_URL || 'http://localhost:3002';

  const getOrderPageUrl = (tableId) => {
    const baseUrl = orderAppUrl.replace(/\/$/, '');
    return `${baseUrl}/tables/${tableId}`;
  };

  // Fetch tables and orders on mount
  useEffect(() => {
    const fetchTables = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await fetch(`${apiUrl}/tables`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await response.json();
        setTableData(data);
      } catch (error) {
        console.error('Error fetching table data:', error);
      }
    };

    const fetchOrders = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await fetch(`${apiUrl}/orders`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (response.status === 401) {
          console.error('Unauthorized – redirecting to login');
          setOrderData([]);
          setLoading(false);
          return;
        }

        const data = await response.json();
        setOrderData(Array.isArray(data) ? data : []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching order data:', error);
        setOrderData([]);
        setLoading(false);
      }
    };

    fetchTables();
    fetchOrders();
  }, [apiUrl]);

  // Determine background color based on orders and table status
  const getBackgroundColor = (tableId) => {
    if (loading) return '#9e9e9e'; // gray while loading

    const table = tableData.find((t) => t._id === tableId);
    if (!table) return '#51910c'; // fallback

    const orders = orderData.filter((o) => o.tableId === tableId);
    if (orders.length > 0) {
      const lastOrder = orders[orders.length - 1];
      // Assuming your order object has an `isTableAvailable` flag.
      // If not, adjust the logic (e.g., check order status).
      return lastOrder.isTableAvailable ? '#ff9800' : '#51910c';
    }
    return '#51910c'; // no orders → available
  };

  const getStatusText = (tableId) => {
    const bg = getBackgroundColor(tableId);
    if (bg === '#51910c') return 'Available';
    if (bg === '#ff9800') return 'Occupied';
    if (bg === '#9e9e9e') return 'Loading...';
    return 'Unknown';
  };

  // Handle table click: if available → open order page & mark occupied
  const handleTableClick = async (tableId) => {
    const bgColor = getBackgroundColor(tableId);
    const isAvailable = bgColor === '#51910c';

    if (isAvailable) {
      try {
        const accessToken = localStorage.getItem('accessToken');
        await fetch(`${apiUrl}/tables/${tableId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ status: 'Occupied' }),
        });

        // 3. Update local state immediately
        setTableData((prev) =>
          prev.map((t) => (t._id === tableId ? { ...t, status: 'Occupied' } : t))
        );
      } catch (error) {
        console.error('Error updating table status:', error);
      }
    } else {
      // For occupied tables, notify parent to show order details
      if (onSelect) {
        onSelect(tableId);
      }
    }

    handleWebsiteView(tableId);
  };

  // Redirect admin to the order app table page.
  const handleWebsiteView = (tableId) => {
    window.location.assign(getOrderPageUrl(tableId));
  };

  // QR code popup handlers
  const openQrPopup = (tableId, e) => {
    e.stopPropagation();
    setSelectedTableId(tableId);
    setQrPopupOpen(true);

    const accessToken = localStorage.getItem('accessToken');
    fetch(`${apiUrl}/tables/qrurl/${tableId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((response) => response.json())
      .then((qrCodeData) => {
        setQRCodeUrl(qrCodeData.qrCodeUrl);
        setTableNo(qrCodeData.tableNo);
      })
      .catch((error) => console.error('Error fetching QR code data:', error));
  };

  const closeQrPopup = () => {
    setQrPopupOpen(false);
    setQRCodeUrl('');
  };

  const printQrCode = () => {
    fetch(`${apiUrl}/tables/qrurlscan/${selectedTableId}`, { method: 'GET' })
      .then((response) => response.json())
      .then((data) => console.log(JSON.stringify(data)))
      .catch((error) => console.error('Error printing QR code:', error));
  };

  return (
    <div
      className="tablecard-container"
      style={{
        position: 'relative',
        width: '100%',
        height: '500px', // Adjust to your desired floor plan area
        border: '1px solid #ccc',
        backgroundColor: '#f8f8f8',
        overflow: 'auto',
      }}
    >
      {tableData.map((table) => {
        const bgColor = getBackgroundColor(table._id);
        const isAvailable = bgColor === '#51910c';
        const tableWidth = table.width;
        const tableHeight = table.shape === 'circle' ? table.width : table.height;

        return (
          <div
            key={table._id}
            className={`table-card-preview ${isAvailable ? 'available' : 'occupied'} ${
              bgColor === '#9e9e9e' ? 'loading' : ''
            }`}
            style={{
              position: 'absolute',
              left: table.x,
              top: table.y,
              width: tableWidth,
              height: tableHeight,
              borderRadius: table.shape === 'circle' ? '50%' : '6px',
              backgroundColor: bgColor,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              padding: '5px',
              textAlign: 'center',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxSizing: 'border-box',
              transition: 'background-color 0.2s',
            }}
            onClick={() => handleTableClick(table._id)}
          >
            <div style={{ fontSize: '14px', marginBottom: '4px' }}>{table.tableno}</div>
            <div
              style={{
                fontSize: '12px',
                marginBottom: '4px',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: 'rgba(255,255,255,0.3)',
              }}
            >
              {getStatusText(table._id)}
            </div>

            {isAvailable && (
              <>
                
                <button
                  style={{
                    fontSize: '12px',
                    padding: '2px 6px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    color: '#51910c',
                    fontWeight: 'bold',
                    marginTop: '4px',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTableClick(table._id);
                  }}
                >
                  Add Order
                </button>
              </>
            )}

            {bgColor === '#ff9800' && (
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                {orderData.filter((o) => o.tableId === table._id).length} order(s)
              </div>
            )}
          </div>
        );
      })}

      {/* QR Code Dialog */}
      <Dialog open={qrPopupOpen} onClose={closeQrPopup} maxWidth="xs" fullWidth>
        <div className="qr-dialog">
          <div className="qr-dialog-header">
            <h3>Table QR Code</h3>
            <IconButton onClick={closeQrPopup} className="close-btn">
              <CloseIcon />
            </IconButton>
          </div>

          <div className="qr-dialog-content">
            <div className="table-info">
              <p>
                Table No: <strong>{tableNo}</strong>
              </p>
            </div>

            {qrCodeUrl && (
              <div className="qr-code-container">
                <QRCode value={qrCodeUrl} size={200} level="H" />
              </div>
            )}

            <div className="qr-dialog-actions">
              <button className="print-qr-btn" onClick={printQrCode}>
                <Print /> Print QR Code
              </button>
              <button
                className="open-website-btn"
                onClick={() => {
                  handleWebsiteView(selectedTableId);
                  closeQrPopup();
                }}
              >
                Open Order Page
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Tablecard;
